<template>
  <ExtendedModal :open="isOpen" :aria-label="shown?.label" @close="closeView()">
    <!-- A related story, or a metal opened from a chip, swaps in place: the key
             change crossfades and rebuilds DetailLayout, so the fresh Lenis viewport
             starts at the top on its own. -->
    <ContentSwap>
      <ArticleView
        v-if="shown?.kind === 'article' && article"
        :key="`extended-article-${article.id}`"
        :article
        zone="article-extended"
        @close="closeView()"
      />
      <MetalView
        v-else-if="shown?.kind === 'metal' && metal"
        :key="`extended-metal-${metal.slug}`"
        :metal
        zone="metal-extended"
        @close="closeView()"
      />
    </ContentSwap>
  </ExtendedModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import ContentSwap from '~/scopes/campaign/components/UI/ContentSwap.vue'
import ExtendedModal from '~/scopes/campaign/components/UI/ExtendedModal.vue'
import MetalView from '~/scopes/campaign/components/highlights/metal/MetalView.vue'
import ArticleView from '~/scopes/campaign/components/impacts/article/ArticleView.vue'
import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MODAL_ROUTES } from '~/scopes/campaign/const/config.const'
import { metalBySlug } from '~/scopes/campaign/data/metals/data'

// One reader, two subjects. `?view=article` and `?view=metal` are branches of a single
// `ExtendedModal` rather than two adapters, because the trip between them is a real one —
// the metal chip inside an article opens that metal *here*. Two cards would cross-dissolve
// two backdrops, and a `backdrop-filter` below full opacity is inert, which is the artefact
// `.claude/rules/article-extended.md` §Transitions exists to avoid. Sharing one card also
// keeps focus restore honest: `ExtendedModal` captures the opener on the `open` false→true
// edge, so leaving after a swap returns to the extend button that started it, not to a chip
// that has since unmounted. Take action and the footer documents stay separate adapters —
// nothing swaps between them, so they'd gain none of this.
const i18n = useI18n()
const {
  activeModal,
  activeDetail,
  activeArticle: article,
  activeReader,
  activeMetalReader,
  quickMetal,
  closeView,
} = useModalState()

// `useIsPhone` is false on the server and on the first client render, and this subtree
// *is* server-rendered (useIntroState seeds seen=true, so CampaignHome emits the full
// UI). Without the mounted flag a phone deep link would SSR the reader and then rip it
// out — so the guard is mandatory here, not merely tidy.
const mounted = ref(false)
const isPhone = useIsPhone()

// Neither branch takes a subject of its own: `?view=` names the mode, the page says what
// it is about. The article comes from the path; the metal from the quick view first and
// the library route's id second — `?metal=` is the topmost panel whenever it is set, so it
// is also the metal a reader grown from there is showing. That is `MetalDetail`'s own
// `props.slug ?? activeDetail`, lifted here because the reader has no panel to hand it one.
const metal = computed(() =>
  metalBySlug(
    quickMetal.value ??
      (activeModal.value === MODAL_ROUTES.METAL_LIBRARY
        ? activeDetail.value
        : null),
  ),
)

type OpenReader = { kind: 'article' | 'metal'; label: string; pageView: string }

// Which branch is open, its accessible name and its page view resolved together — three
// answers to one question, so they cannot disagree. Each branch answers to its own
// `?view=` value, which is what makes `/metal/gold?view=article` open nothing on the value
// alone; the route guards are the belt and braces, since article and metal slugs share
// `route.params.id` and are now both kebab-case — `activeArticle` carries that guard.
const reader = computed<OpenReader | null>(() => {
  if (activeReader.value && article.value)
    return {
      kind: 'article',
      label: i18n(article.value.title),
      pageView: `article-${article.value.id}-extended`,
    }
  if (activeMetalReader.value && metal.value)
    return {
      kind: 'metal',
      label: i18n(metal.value.name),
      pageView: `metal-${metal.value.slug}-extended`,
    }
  return null
})

const isOpen = computed(
  () => mounted.value && !isPhone.value && reader.value !== null,
)

// What the card renders, which is not the same as what is open: closing drops `?view`
// immediately, so binding to `reader` would empty the card — header, ✕ and all — on the
// first frame of a 400ms fade. This holds the last branch through the leave, and is only
// ever replaced by the other. Seeded rather than left null, so a deep link isn't an open
// card with nothing in it. (A single-branch adapter needs no latch: its `v-if` rides the
// subject, and the subject outlives the close. A second branch is what moves the decision
// onto a value that doesn't.)
const shown = ref(reader.value)
watch(reader, (value) => {
  if (value) shown.value = value
})

// Watching the open subject rather than mount: the card reopens many times per session,
// and both a related story and a metal chip swap its content without remounting us.
// Registered on mount so it stays out of SSR, `immediate` so a deep link — open at first
// render, so never a change — is counted too. Kept here rather than in the shared views:
// the panels stay mounted underneath, so a page view down there would fire twice.
onMounted(() => {
  mounted.value = true
})
</script>
